import { polygonToBounds } from './geometry.ts'
import {
  isGeometryType,
  isLatLng,
  isLatLngBounds,
  type GeometryType,
  type LatLng,
  type LatLngBounds,
} from './types.ts'

export function parseWKTPoint(wkt: string): LatLng | null {
  const match = wkt.match(/POINT\(([^ ]+) ([^ ]+)\)/)
  if (!match) {
    return null
  }

  return {
    lng: Number.parseFloat(match[1]), // PostGIS returns as (longitude latitude)
    lat: Number.parseFloat(match[2]),
  }
}

export function parseWKTPolygon(wkt: string, polygons = false): LatLng[] | LatLngBounds | null {
  const match = wkt.match(/POLYGON\(\((.*)\)\)/)
  if (!match) {
    return null
  }

  const points = match[1].split(',').map((coord) => {
    const [lng, lat] = coord.trim().split(' ')
    return { lng: Number.parseFloat(lng), lat: Number.parseFloat(lat) }
  })

  // If polygons is true, always return points array
  if (polygons) {
    return points
  }

  // Convert small polygons to bounding boxes
  if (points.length <= 5) {
    return polygonToBounds(points)
  }

  // If it's not a bounding box, return as regular polygon
  return points
}

export function parseWKTMultiPolygon(
  wkt: string,
  polygons = false
): (LatLng[] | LatLngBounds)[] | LatLng[][] | null {
  const matches = wkt.match(/MULTIPOLYGON\((.*)\)/)
  if (!matches) {
    return null
  }

  // Split into individual polygons, handling nested parentheses
  const polygonStrings: string[] = []
  let currentPolygon = ''
  let depth = 0

  for (const char of matches[1]) {
    if (char === '(') {
      depth++
      if (depth === 3) {
        // Start of polygon points
        currentPolygon = ''
      } else {
        currentPolygon += char
      }
    } else if (char === ')') {
      depth--
      if (depth === 2) {
        // End of polygon points
        polygonStrings.push(currentPolygon)
      } else {
        currentPolygon += char
      }
    } else if (depth === 3) {
      currentPolygon += char
    }
  }

  const result = polygonStrings
    .map((polyStr) => {
      const points = polyStr.split(',').map((coord) => {
        const [lng, lat] = coord.trim().split(' ')
        return { lng: Number.parseFloat(lng), lat: Number.parseFloat(lat) }
      })

      // If polygons is true, always return points array
      if (polygons) {
        return points
      }

      // Try to convert to bounding box if it's a simple rectangle
      const bounds = points.length <= 5 ? polygonToBounds(points) : null
      return bounds || points
    })
    .filter((geom): geom is LatLng[] | LatLngBounds => geom !== null)

  // If no valid polygons found
  if (result.length === 0) {
    return null
  }

  // If polygons is true, return array of point arrays
  if (polygons) {
    return result.filter(isLatLngArray)
  }

  // If all are bounds, return array of bounds
  if (result.every(isLatLngBounds)) {
    return result.filter((b): b is LatLngBounds => isLatLngBounds(b))
  }

  // Otherwise return array of mixed types
  return result
}

export function parseWKTBox(wkt: string): LatLngBounds | null {
  const match = wkt.match(/BOX2D\(([^ ]+) ([^ ]+),([^ ]+) ([^ ]+)\)/)
  if (!match) {
    return null
  }

  return {
    west: Number.parseFloat(match[1]),
    south: Number.parseFloat(match[2]),
    east: Number.parseFloat(match[3]),
    north: Number.parseFloat(match[4]),
  }
}

export type GeoJSONPoint = {
  type: 'Point'
  coordinates: [number, number]
  crs?: { type: 'name'; properties: { name: string } }
}

export type GeoJSONPolygon = {
  type: 'Polygon'
  coordinates: [number, number][][]
  crs?: { type: 'name'; properties: { name: string } }
}

export type GeoJSONMultiPolygon = {
  type: 'MultiPolygon'
  coordinates: [number, number][][][]
  crs?: { type: 'name'; properties: { name: string } }
}

export type GeoJSONBox = {
  type: 'Box'
  bounds: [[number, number], [number, number]]
  crs?: { type: 'name'; properties: { name: string } }
}

export type GeoJSON = GeoJSONPoint | GeoJSONPolygon | GeoJSONMultiPolygon | GeoJSONBox

export function parseGeomString(geom: string, polygons = false): GeometryType | null {
  if (geom.includes('POINT')) {
    return parseWKTPoint(geom)
  }

  if (geom.includes('MULTIPOLYGON')) {
    const result = parseWKTMultiPolygon(geom, polygons)
    if (!result) {
      return null
    }

    // If polygons is true, return array of point arrays
    if (polygons) {
      return result as LatLng[][]
    }

    // If all are bounds, return array of bounds
    if (result.every(isLatLngBounds)) {
      return result as LatLngBounds[]
    }

    // Otherwise return the first polygon/bounds
    return result[0]
  }

  if (geom.includes('POLYGON')) {
    return parseWKTPolygon(geom, polygons)
  }

  if (geom.includes('BOX2D')) {
    return parseWKTBox(geom)
  }

  return null
}

function isLatLngArray(value: unknown): value is LatLng[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    isLatLng(value[0])
  )
}

export function parseGeoJSON(geom: GeoJSON, polygons = false): GeometryType | null {
  if (geom.type === 'Point') {
    const [lng, lat] = geom.coordinates
    return { lng, lat } as LatLng
  }

  if (geom.type === 'MultiPolygon') {
    // Convert each polygon to points or bounds
    const result = geom.coordinates.map((polygon) => {
      // Each polygon is an array of linear rings, we take the first (outer) ring
      const points = polygon[0].map(([lng, lat]) => ({ lng, lat }) as LatLng)

      // If polygons is true, always return points array
      if (polygons) {
        return points
      }

      // Try to convert to bounding box if it's a simple rectangle
      const bounds = points.length <= 5 ? polygonToBounds(points) : null
      return bounds || points
    })

    // If polygons is true, return array of point arrays
    if (polygons) {
      return result as LatLng[][]
    }

    // If all are bounds, return array of bounds
    if (result.every(isLatLngBounds)) {
      return result as LatLngBounds[]
    }

    // Otherwise return the first polygon/bounds
    const firstResult = result[0]

    if (isLatLngArray(firstResult) || isLatLngBounds(firstResult)) {
      return firstResult
    }

    return null
  }

  if (geom.type === 'Polygon') {
    const points = geom.coordinates[0].map(([lng, lat]) => ({ lng, lat }))

    // If polygons is true, always return points array
    if (polygons) {
      return points
    }

    return polygonToBounds(points) || points
  }

  if (geom.type === 'Box') {
    const [[west, south], [east, north]] = geom.bounds
    return { east, north, south, west }
  }

  return null
}

export function parseGeom(
  geom: string | GeoJSON | GeoJSON[] | GeometryType | null | undefined,
  polygons = false
): GeometryType | null {
  if (!geom) {
    return null
  }

  if (typeof geom === 'string') {
    return parseGeomString(geom, polygons)
  }

  if (isGeometryType(geom)) {
    return geom
  }

  if (Array.isArray(geom)) {
    // If we get an array of GeoJSON objects, parse each one
    const result = geom.map((g) => parseGeoJSON(g, polygons))

    // Filter out nulls
    const validResults = result.filter((g): g is GeometryType => g !== null)

    if (validResults.length === 0) {
      return null
    }

    // If polygons is true, return array of point arrays
    if (polygons) {
      const polygonArray = validResults.filter(
        (g): g is LatLng[] => Array.isArray(g) && g.some(isLatLng)
      )

      return polygonArray.length > 0 ? polygonArray : null
    }

    // If all are bounds, return array of bounds
    if (validResults.every(isLatLngBounds)) {
      return validResults.filter((b): b is LatLngBounds => isLatLngBounds(b))
    }

    // Otherwise return the first valid result
    return validResults[0]
  }

  return parseGeoJSON(geom, polygons)
}

export function createGeomString(geometry: GeometryType): string | null {
  if (isLatLngBounds(geometry)) {
    // For bounding boxes, create a polygon representation
    const points = [
      `${geometry.west} ${geometry.south}`, // SW
      `${geometry.west} ${geometry.north}`, // NW
      `${geometry.east} ${geometry.north}`, // NE
      `${geometry.east} ${geometry.south}`, // SE
      `${geometry.west} ${geometry.south}`, // SW again to close the polygon
    ].join(',')

    return `SRID=4326;POLYGON((${points}))`
  }

  if (Array.isArray(geometry)) {
    if (geometry.length === 0) {
      return null
    }

    // Check if it's an array of bounding boxes
    if (geometry.every(isLatLngBounds)) {
      const polygons = (geometry as LatLngBounds[]).map((bounds) => {
        const points = [
          `${bounds.west} ${bounds.south}`,
          `${bounds.west} ${bounds.north}`,
          `${bounds.east} ${bounds.north}`,
          `${bounds.east} ${bounds.south}`,
          `${bounds.west} ${bounds.south}`,
        ].join(',')
        return `((${points}))`
      })

      return `SRID=4326;MULTIPOLYGON(${polygons.join(',')})`
    }

    // For polygons, we need to close the ring by repeating the first point
    const points = [...geometry]

    if (points[0] !== points.at(-1)) {
      points.push(points[0])
    }

    const pointsStr = points
      .filter(isLatLng)
      .map((point) => `${point.lng} ${point.lat}`)
      .join(',')

    return `SRID=4326;POLYGON((${pointsStr}))`
  }

  // For single points
  return `SRID=4326;POINT(${geometry.lng} ${geometry.lat})`
}

export function geometriesAreEqual(geom1: GeometryType, geom2: GeometryType) {
  const geom1String = createGeomString(geom1)
  const geom2String = createGeomString(geom2)
  return !!geom1String && !!geom2String && geom1String === geom2String
}

export function getLatLngFromGeometry(geometry: GeometryType | null): LatLng | null {
  if (!geometry) {
    return null
  }

  if (isLatLng(geometry)) {
    return geometry
  }

  if (isLatLngBounds(geometry)) {
    return {
      lat: (geometry.north + geometry.south) / 2,
      lng: (geometry.east + geometry.west) / 2,
    }
  }

  if (Array.isArray(geometry)) {
    if (geometry.length === 0) {
      return null
    }

    const firstItem = geometry[0]

    if (isLatLng(firstItem)) {
      return firstItem
    }

    if (isLatLngBounds(firstItem)) {
      return {
        lat: (firstItem.north + firstItem.south) / 2,
        lng: (firstItem.east + firstItem.west) / 2,
      }
    }

    if (Array.isArray(firstItem) && firstItem.length > 0 && isLatLng(firstItem[0])) {
      return firstItem[0]
    }
  }

  return null
}
